import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { format, addDays, differenceInDays } from 'date-fns';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';

import { Card, CardContent } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/molecules/popover';
import { Calendar } from '@/components/atoms/calendar';
import { cn, formatPrice } from '@/lib/utils';
import { CURRENCY_SYMBOLS, type Currency } from '@/lib/utils/constants';
import type { AdType, AdTypeConfig } from '../../../types/ads.types';
import {
  NotificationPricingCard,
  PerDayPricingCard,
  PerWeekPricingCard,
} from '../shared/PricingCard';
import {
  isNotificationType,
  isPerDayType,
  isFeaturedShopType,
  isCpcType,
  fromEur,
  calculateCampaignCost,
  getBudgetPresets,
  getBudgetLimits,
  getMinBidAmount,
  getTypeConfig,
} from '../../../utils/pricing.utils';

// ============ Types ============

interface StepBudgetDatesProps {
  campaignType: AdType;
  budget: number;
  onBudgetChange: (budget: number) => void;
  dailyBudget: number | null;
  onDailyBudgetChange: (budget: number | null) => void;
  bidAmount: number | null;
  onBidAmountChange: (amount: number | null) => void;
  startDate: string;
  onStartDateChange: (date: string) => void;
  endDate: string;
  onEndDateChange: (date: string) => void;
  currency: Currency;
  typeConfigs?: AdTypeConfig[];
  audienceCount?: number;
  isLoadingAudience?: boolean;
}

// ============ Component ============

export function StepBudgetDates({
  campaignType,
  budget,
  onBudgetChange,
  dailyBudget,
  onDailyBudgetChange,
  bidAmount,
  onBidAmountChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  currency,
  typeConfigs,
  audienceCount = 0,
  isLoadingAudience = false,
}: StepBudgetDatesProps) {
  const { t } = useTranslation();

  // Helper to extract time from ISO date string (in UTC)
  const extractTime = (dateStr: string): string => {
    if (!dateStr) return '00:00';
    try {
      // Extract time directly from ISO string to avoid timezone conversion
      const match = dateStr.match(/T(\d{2}):(\d{2})/);
      if (match) {
        return `${match[1]}:${match[2]}`;
      }
      // Fallback: parse date and get UTC hours/minutes
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '00:00';
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch {
      return '00:00';
    }
  };

  // Helper to combine date and time into ISO string
  const combineDateAndTime = (dateStr: string, time: string): string => {
    if (!dateStr) return '';
    const datePart = dateStr.split('T')[0];
    return `${datePart}T${time}:00.000Z`;
  };

  // Local state for times
  const [startTime, setStartTime] = React.useState(() => extractTime(startDate));
  const [endTime, setEndTime] = React.useState(() => extractTime(endDate) || '23:59');

  // Sync times when props change
  React.useEffect(() => {
    if (startDate) {
      const time = extractTime(startDate);
      if (time !== '00:00' || !startTime) setStartTime(time);
    }
  }, [startDate]);

  React.useEffect(() => {
    if (endDate) {
      const time = extractTime(endDate);
      if (time !== '00:00' || !endTime) setEndTime(time);
    }
  }, [endDate]);

  const typeConfig = getTypeConfig(campaignType, typeConfigs);
  const isNotification = isNotificationType(campaignType);
  const isPerDay = isPerDayType(campaignType);
  const isFeaturedShop = isFeaturedShopType(campaignType);
  const isCpc = isCpcType(campaignType);

  // Calculate days and weeks (ignore time, compare dates only)
  const days = React.useMemo(() => {
    if (!startDate || !endDate) return 0;
    // Extract date parts only to avoid time affecting the calculation
    const startDateOnly = startDate.split('T')[0];
    const endDateOnly = endDate.split('T')[0];
    return differenceInDays(new Date(endDateOnly), new Date(startDateOnly)) + 1;
  }, [startDate, endDate]);

  const weeks = Math.ceil(days / 7);

  // Calculate cost
  const { calculatedCost, isBelowMinimum, minBudgetInCurrency } = React.useMemo(
    () =>
      calculateCampaignCost(campaignType, typeConfig, currency, {
        audienceCount,
        startDate,
        endDate,
        budget,
      }),
    [campaignType, typeConfig, currency, audienceCount, startDate, endDate, budget]
  );

  // Auto-update budget for auto-calculated types
  React.useEffect(() => {
    const shouldAutoCalculate = isNotification || isPerDay || isFeaturedShop;
    if (shouldAutoCalculate && calculatedCost !== null && calculatedCost !== budget) {
      onBudgetChange(calculatedCost);
    }
  }, [isNotification, isPerDay, isFeaturedShop, calculatedCost, budget, onBudgetChange]);

  // Budget presets and limits for CPC
  const budgetPresets = getBudgetPresets(currency);
  const { min: minBudget, max: maxBudget, step } = getBudgetLimits(currency);
  const minBid = getMinBidAmount(typeConfig, currency);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center">
        <h2 className="text-lg sm:text-xl font-semibold mb-1 sm:mb-2">
          {isNotification
            ? t('ads.wizard.step3.titleNotif', 'Coût et envoi')
            : t('ads.wizard.step3.title', 'Budget et durée')}
        </h2>
        <p className="text-sm text-muted-foreground hidden sm:block">
          {isNotification
            ? t('ads.wizard.step3.subtitleNotif', 'Vérifiez le coût et choisissez quand envoyer')
            : t('ads.wizard.step3.subtitle', 'Définissez combien et pendant combien de temps')}
        </p>
      </div>

      {/* Notification pricing */}
      {isNotification && typeConfig && (
        <NotificationPricingCard
          audienceCount={audienceCount}
          pricePerThousand={fromEur(typeConfig.priceEur, currency)}
          calculatedCost={calculatedCost}
          currency={currency}
          isLoading={isLoadingAudience}
          isBelowMinimum={isBelowMinimum}
          minBudget={minBudgetInCurrency}
          audienceLabel={
            campaignType === 'push_shop_subscribers'
              ? 'Vos abonnés'
              : 'Personnes qui ont aimé vos produits'
          }
        />
      )}

      {/* Per-day pricing (Carousel, Category Banner) */}
      {isPerDay && typeConfig && (
        <PerDayPricingCard
          pricePerDay={fromEur(typeConfig.priceEur, currency)}
          days={days}
          calculatedCost={calculatedCost}
          currency={currency}
        />
      )}

      {/* Featured Shop pricing (per week) */}
      {isFeaturedShop && typeConfig && (
        <PerWeekPricingCard
          pricePerWeek={fromEur(typeConfig.priceEur, currency)}
          weeks={weeks}
          calculatedCost={calculatedCost}
          currency={currency}
        />
      )}

      {/* CPC Budget section */}
      {isCpc && (
        <div className="space-y-3 sm:space-y-4">
          {/* Bid amount */}
          <div className="space-y-1.5 sm:space-y-2">
            <Label className="text-sm">
              Enchère par clic
              <span className="text-destructive ml-1">*</span>
            </Label>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Plus votre enchère est élevée, plus votre produit sera visible
            </p>
            <div className="relative">
              <Input
                type="number"
                min={minBid}
                step={currency === 'KMF' ? 5 : 0.01}
                value={bidAmount ?? ''}
                onChange={(e) =>
                  onBidAmountChange(e.target.value ? Number(e.target.value) : null)
                }
                placeholder={`Min: ${formatPrice(minBid, currency)}`}
                className="pr-12"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {CURRENCY_SYMBOLS[currency]}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Enchère minimum: {formatPrice(minBid, currency)}/clic
            </p>
          </div>

          {/* Budget total */}
          <div className="space-y-1 sm:space-y-2">
            <Label className="text-sm">
              Budget maximum
              <span className="text-destructive ml-1">*</span>
            </Label>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Vous ne dépenserez jamais plus que ce montant
            </p>
          </div>

          <div className="space-y-3 sm:space-y-4">
            <div className="text-center">
              <span className="text-3xl sm:text-4xl font-bold">
                {formatPrice(budget, currency)}
              </span>
            </div>
            <input
              type="range"
              min={minBudget}
              max={maxBudget}
              step={step}
              value={budget}
              onChange={(e) => onBudgetChange(Number(e.target.value))}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{formatPrice(minBudget, currency)}</span>
              <span>{formatPrice(maxBudget, currency)}</span>
            </div>
          </div>

          {/* Quick presets */}
          <div className="flex flex-wrap gap-2">
            {budgetPresets.map((preset) => (
              <Button
                key={preset}
                type="button"
                variant={budget === preset ? 'default' : 'outline'}
                size="sm"
                onClick={() => onBudgetChange(preset)}
              >
                {formatPrice(preset, currency)}
              </Button>
            ))}
          </div>

          {/* Daily budget */}
          <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t">
            <div className="flex items-center justify-between">
              <Label htmlFor="dailyBudget" className="text-sm">
                Limite par jour
                <span className="text-muted-foreground text-xs sm:text-sm ml-1 sm:ml-2">
                  (optionnel)
                </span>
              </Label>
              {dailyBudget !== null && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onDailyBudgetChange(null)}
                >
                  Retirer
                </Button>
              )}
            </div>
            <div className="relative">
              <Input
                id="dailyBudget"
                type="number"
                min={0}
                step={currency === 'KMF' ? 100 : 0.5}
                value={dailyBudget ?? ''}
                onChange={(e) =>
                  onDailyBudgetChange(e.target.value ? Number(e.target.value) : null)
                }
                placeholder="Ex: 5"
                className="pr-12"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {CURRENCY_SYMBOLS[currency]}
              </span>
            </div>
          </div>

          {/* Estimated clicks */}
          {bidAmount && bidAmount > 0 && budget > 0 && (
            <Card className="bg-muted/50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Clics estimés</span>
                  <span className="text-xl font-bold">
                    ~{Math.floor(budget / bidAmount).toLocaleString()} clics
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Dates */}
      <div className="space-y-3 sm:space-y-4">
        <div className="grid gap-3 sm:gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:space-y-2">
            <Label className="text-sm">
              {isNotification ? "Date d'envoi" : 'Date de début'}
              <span className="text-destructive ml-1">*</span>
            </Label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'flex-1 justify-start text-left font-normal',
                      !startDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(new Date(startDate), 'dd/MM/yyyy') : 'Date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate ? new Date(startDate) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        const dateStr = format(date, 'yyyy-MM-dd');
                        onStartDateChange(combineDateAndTime(dateStr, startTime));
                      } else {
                        onStartDateChange('');
                      }
                    }}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <div className="relative w-28 sm:w-32">
                <Clock className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground dark:text-white pointer-events-none" />
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => {
                    const newTime = e.target.value;
                    setStartTime(newTime);
                    if (startDate) {
                      onStartDateChange(combineDateAndTime(startDate, newTime));
                    }
                  }}
                  className="pl-8 text-sm"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Heure UTC</p>
          </div>

          {!isNotification && (
            <div className="space-y-1.5 sm:space-y-2">
              <Label className="text-sm">
                Date de fin
                <span className="text-destructive ml-1">*</span>
              </Label>
              <div className="flex gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'flex-1 justify-start text-left font-normal',
                        !endDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(new Date(endDate), 'dd/MM/yyyy') : 'Date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate ? new Date(endDate) : undefined}
                      onSelect={(date) => {
                        if (date) {
                          const dateStr = format(date, 'yyyy-MM-dd');
                          onEndDateChange(combineDateAndTime(dateStr, endTime));
                        } else {
                          onEndDateChange('');
                        }
                      }}
                      disabled={(date) =>
                        date < new Date() ||
                        (startDate ? date < new Date(startDate) : false)
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <div className="relative w-28 sm:w-32">
                  <Clock className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground dark:text-white pointer-events-none" />
                  <Input
                    type="time"
                    value={endTime}
                    onChange={(e) => {
                      const newTime = e.target.value;
                      setEndTime(newTime);
                      if (endDate) {
                        onEndDateChange(combineDateAndTime(endDate, newTime));
                      }
                    }}
                    className="pl-8 text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Heure UTC</p>
            </div>
          )}
        </div>

        {/* Quick duration presets */}
        {!isNotification && startDate && (
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            <span className="text-xs sm:text-sm text-muted-foreground mr-1 sm:mr-2 flex items-center">
              Durée:
            </span>
            {[7, 14, 30].map((d) => {
              const isSelected = days === d;
              return (
                <Button
                  key={d}
                  type="button"
                  variant={isSelected ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    const end = addDays(new Date(startDate), d - 1);
                    onEndDateChange(combineDateAndTime(format(end, 'yyyy-MM-dd'), endTime));
                  }}
                >
                  {d} jours
                </Button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
